import { useEffect, useRef, useState } from 'react';
import { useFilters } from '../lib/filters.js';
import {
  KPI_CATALOG,
  KPI_IDS,
  SECCION_CATALOG,
  SECCION_IDS,
  sortKpis,
  sortSecciones,
  type KpiId,
  type SeccionId,
} from '../lib/vista.js';

/**
 * Selector de QUÉ SE VE en el dashboard: las tarjetas KPI de arriba y las áreas
 * de abajo (gráficos, tablas y paneles), en una sola lista con dos grupos.
 *
 * La selección vive en la URL (§10), así que es deep-linkable y una vista
 * guardada (SavedViews serializa el query completo) se la lleva.
 */
export function VistaPicker() {
  const [f, update] = useFilters();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggleKpi(id: KpiId) {
    const next = f.kpis.includes(id) ? f.kpis.filter((x) => x !== id) : [...f.kpis, id];
    update({ kpis: sortKpis(next) });
  }

  function toggleSeccion(id: SeccionId) {
    const next = f.secciones.includes(id)
      ? f.secciones.filter((x) => x !== id)
      : [...f.secciones, id];
    update({ secciones: sortSecciones(next) });
  }

  const visibles = f.kpis.length + f.secciones.length;
  const totales = KPI_IDS.length + SECCION_IDS.length;

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:border-slate-600 hover:text-slate-100"
      >
        Vista · {visibles}/{totales} ▾
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 max-h-[70vh] w-72 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 p-2 shadow-xl">
          <p className="px-2 pb-1 text-[11px] uppercase tracking-wide text-slate-500">
            Indicadores
          </p>
          {KPI_CATALOG.map((k) => (
            <label
              key={k.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-slate-200 hover:bg-slate-800"
            >
              <input
                type="checkbox"
                checked={f.kpis.includes(k.id)}
                onChange={() => toggleKpi(k.id)}
              />
              {k.label}
            </label>
          ))}

          <p className="mt-2 border-t border-slate-800 px-2 pb-1 pt-2 text-[11px] uppercase tracking-wide text-slate-500">
            Áreas
          </p>
          {SECCION_CATALOG.map((s) => (
            <label
              key={s.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-slate-200 hover:bg-slate-800"
            >
              <input
                type="checkbox"
                checked={f.secciones.includes(s.id)}
                onChange={() => toggleSeccion(s.id)}
              />
              {s.label}
            </label>
          ))}

          <div className="mt-1 flex justify-between border-t border-slate-800 px-2 pt-2 text-xs">
            <button
              onClick={() => update({ kpis: [...KPI_IDS], secciones: [...SECCION_IDS] })}
              className="text-slate-400 hover:text-blue-300"
            >
              Todo
            </button>
            <button
              onClick={() => update({ kpis: [], secciones: [] })}
              className="text-slate-400 hover:text-blue-300"
            >
              Nada
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
